
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/22b1e345-a75d-47ba-94cf-08e446d9b10f.png" 
                alt="ZAGI DOO BOR Logo" 
                className="h-10 w-10 object-contain"
              />
              <span className={`font-semibold text-lg whitespace-nowrap transition-colors ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}>ZAGI DOO BOR</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <button 
                onClick={() => scrollToSection('vpn')}
                className={`text-sm font-light transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white/90 hover:text-white'
                }`}
              >
                VPN
              </button>
              <button 
                onClick={() => scrollToSection('security')}
                className={`text-sm font-light transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white/90 hover:text-white'
                }`}
              >
                Security
              </button>
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className={`text-sm font-light transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white/90 hover:text-white'
                }`}
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className={`text-sm font-light transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-700 hover:text-cyan-500' : 'text-white/90 hover:text-white'
                }`}
              >
                Contact
              </button>
            </div>

            <button className="px-6 py-2.5 bg-cyan-500 text-white text-sm font-medium rounded-full hover:bg-cyan-600 transition-all hover:shadow-lg hover:shadow-cyan-500/30 whitespace-nowrap">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#0A1628] via-[#1E3A5F] to-[#0A1628]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-1 bg-cyan-500 mb-8"></div>
              <h1 className="text-6xl md:text-7xl font-light text-white mb-2">
                Secure Your
              </h1>
              <h1 className="text-7xl md:text-8xl font-bold text-white mb-2">
                Digital Life
              </h1>
              <h1 className="text-6xl md:text-7xl font-light text-white mb-8">
                Everywhere
              </h1>
              
              <p className="text-lg text-[#B8C5D6] mb-4 leading-relaxed max-w-xl">
                Experience ultimate online privacy and security with ZAGI DOO BOR VPN. 
              </p>
              <p className="text-lg text-[#B8C5D6] mb-10 leading-relaxed max-w-xl">
                Military-grade encryption, lightning-fast speeds, and zero-logs policy to protect your digital freedom.
              </p>

              <div className="flex flex-wrap gap-4">
                <button className="px-8 py-4 bg-cyan-500 text-white font-medium rounded-lg hover:bg-cyan-600 transition-all hover:shadow-xl hover:shadow-cyan-500/30 flex items-center gap-2 whitespace-nowrap">
                  <i className="ri-download-line text-xl"></i>
                  Download Now
                </button>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="px-8 py-4 bg-transparent border-2 border-cyan-500 text-cyan-500 font-medium rounded-lg hover:bg-cyan-500 hover:text-white transition-all flex items-center gap-2 whitespace-nowrap"
                >
                  Learn More
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="relative hidden md:block">
              <div className="relative w-full h-[500px]">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-br from-cyan-500/30 to-purple-500/30 rounded-full blur-2xl animate-pulse"></div>
                <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-cyan-500/40 rounded-full blur-xl animate-bounce" style={{ animationDuration: '3s' }}></div>
                <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-purple-500/40 rounded-full blur-xl animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
                
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-4 border-cyan-500/30 rounded-3xl rotate-45 animate-spin" style={{ animationDuration: '20s' }}></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border-2 border-purple-500/20 rounded-full"></div>
                
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-cyan-400">
                  <i className="ri-shield-check-line text-9xl drop-shadow-2xl"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-16 items-center">
            <div className="md:col-span-2 relative">
              <div className="relative w-full h-[500px] bg-gradient-to-br from-cyan-50 to-purple-50 rounded-3xl overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-3xl rotate-6 blur-xl"></div>
                    <div className="relative w-64 h-64 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl">
                      <i className="ri-shield-keyhole-line text-8xl text-white"></i>
                    </div>
                  </div>
                </div>
                <div className="absolute top-10 right-10 w-32 h-32 bg-cyan-500/10 rounded-2xl rotate-12"></div>
                <div className="absolute bottom-10 left-10 w-24 h-24 bg-purple-500/10 rounded-2xl -rotate-12"></div>
              </div>
            </div>

            <div className="md:col-span-3">
              <div className="text-cyan-500 text-sm font-semibold tracking-wider uppercase mb-6">
                ABOUT ZAGI DOO BOR
              </div>
              <h2 className="text-5xl md:text-6xl font-light text-[#0A1628] mb-2">
                Protecting Your
              </h2>
              <h2 className="text-5xl md:text-6xl font-bold text-[#0A1628] mb-2">
                Privacy Since
              </h2>
              <h2 className="text-5xl md:text-6xl font-light text-[#0A1628] mb-10">
                Day One
              </h2>

              <div className="space-y-6 text-[#5A6C7D] leading-relaxed">
                <p className="text-lg">
                  ZAGI DOO BOR is a leading software development company based in Bor, Serbia, dedicated to creating innovative applications that enhance digital security and user experience. Our flagship VPN solution represents our commitment to protecting online privacy in an increasingly connected world.
                </p>
                <p className="text-lg">
                  We believe that everyone deserves the right to browse the internet freely and securely, without compromising their personal data. Our VPN technology combines cutting-edge encryption protocols with user-friendly design, making advanced security accessible to everyone.
                </p>
                <p className="text-lg">
                  With servers strategically located around the globe and a strict no-logs policy, we ensure that your online activities remain private and your data stays yours. Trust ZAGI DOO BOR to be your partner in digital freedom.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VPN Features Section */}
      <section id="vpn" className="py-32 bg-[#F5F8FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <div className="text-cyan-500 text-sm font-semibold tracking-wider uppercase mb-6">
              FEATURES
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-[#0A1628] mb-2">
              Why Choose
            </h2>
            <h2 className="text-5xl md:text-6xl font-bold text-[#0A1628]">
              Our VPN Solution
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-lock-line',
                title: 'Military-Grade Encryption',
                description: 'AES-256 encryption ensures your data is protected with the same security standards used by governments and military organizations worldwide.'
              },
              {
                icon: 'ri-speed-line',
                title: 'Lightning-Fast Speeds',
                description: 'Optimized servers and advanced protocols deliver blazing-fast connection speeds without compromising your security or privacy.'
              },
              {
                icon: 'ri-global-line',
                title: 'Global Server Network',
                description: 'Access content from anywhere with our extensive network of high-performance servers strategically located across the globe.'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Zero-Logs Policy',
                description: 'We never track, collect, or store your online activities. Your privacy is our priority, and we have nothing to share with anyone.'
              },
              {
                icon: 'ri-shut-down-line',
                title: 'Kill Switch Protection',
                description: 'Automatic kill switch instantly disconnects your internet if VPN connection drops, ensuring your data never leaks accidentally.'
              },
              {
                icon: 'ri-customer-service-2-line',
                title: '24/7 Support',
                description: 'Our dedicated support team is available around the clock to assist you with any questions or technical issues you may encounter.'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-4">
                  {feature.title}
                </h3>
                <p className="text-[#5A6C7D] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-32 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/50 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <div className="text-cyan-500 text-sm font-semibold tracking-wider uppercase mb-6">
              HOW IT WORKS
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-[#0A1628] mb-2">
              Get Protected in
            </h2>
            <h2 className="text-5xl md:text-6xl font-bold text-[#0A1628]">
              Three Simple Steps
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting Lines */}
            <div className="hidden md:block absolute top-32 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-cyan-500 to-purple-500"></div>

            {[
              {
                number: '1',
                title: 'Download & Install',
                icon: 'ri-download-cloud-line',
                description: 'Download our VPN application for your device and complete the quick installation process in just a few clicks.'
              },
              {
                number: '2',
                title: 'Create Account',
                icon: 'ri-user-add-line',
                description: 'Sign up with your email address and choose your preferred subscription plan to get started with secure browsing.'
              },
              {
                number: '3',
                title: 'Connect & Browse',
                icon: 'ri-shield-check-line',
                description: 'Select a server location, click connect, and enjoy secure, private, and unrestricted internet access instantly.'
              }
            ].map((step, index) => (
              <div 
                key={index}
                className={`relative bg-gradient-to-br from-white to-cyan-50/30 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 ${
                  index === 1 ? 'md:-translate-y-4' : ''
                }`}
              >
                <div className="absolute -top-6 -left-6 w-16 h-16 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-full flex items-center justify-center shadow-xl">
                  <span className="text-3xl font-bold text-white">{step.number}</span>
                </div>

                <div className="mt-8 mb-6">
                  <div className="w-32 h-32 mx-auto bg-gradient-to-br from-cyan-100 to-purple-100 rounded-2xl flex items-center justify-center">
                    <i className={`${step.icon} text-6xl text-cyan-600`}></i>
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-[#0A1628] mb-4 text-center">
                  {step.title}
                </h3>
                <p className="text-[#5A6C7D] text-center leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Privacy Section */}
      <section id="security" className="py-32 bg-[#F5F8FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-11 gap-16 items-center">
            <div className="md:col-span-6">
              <div className="text-cyan-500 text-sm font-semibold tracking-wider uppercase mb-6">
                SECURITY & PRIVACY
              </div>
              <h2 className="text-5xl md:text-6xl font-light text-[#0A1628] mb-2">
                Your Data
              </h2>
              <h2 className="text-5xl md:text-6xl font-bold text-[#0A1628] mb-2">
                Stays Yours
              </h2>
              <h2 className="text-5xl md:text-6xl font-light text-[#0A1628] mb-2">
                Always
              </h2>
              <h2 className="text-5xl md:text-6xl font-bold text-[#0A1628] mb-10">
                Encrypted
              </h2>

              <div className="space-y-6 mb-10">
                {[
                  {
                    title: 'AES-256 Encryption',
                    description: 'Industry-leading encryption standard that protects your data from any unauthorized access or interception.'
                  },
                  {
                    title: 'No Activity Logs',
                    description: 'We maintain a strict zero-logs policy, meaning we never record your browsing history, connection times, or IP addresses.'
                  },
                  {
                    title: 'Automatic Kill Switch',
                    description: 'Instantly blocks all internet traffic if your VPN connection drops, preventing any data leaks or exposure.'
                  },
                  {
                    title: 'DNS Leak Protection',
                    description: 'Advanced DNS leak protection ensures your DNS queries are always encrypted and routed through our secure servers.'
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-[#0A1628] mb-2">
                        {item.title}
                      </h4>
                      <p className="text-[#5A6C7D] leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <button className="px-8 py-4 bg-[#0A1628] text-white font-medium rounded-lg hover:bg-[#1E3A5F] transition-all hover:shadow-xl flex items-center gap-2 whitespace-nowrap">
                <i className="ri-lock-line text-xl"></i>
                View Security Details
              </button>
            </div>

            <div className="md:col-span-5 relative">
              <div className="relative w-full h-[600px]">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 rounded-3xl"></div>
                
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 border-4 border-cyan-500/30 rounded-3xl rotate-45 animate-spin" style={{ animationDuration: '20s' }}></div>
                
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-3xl backdrop-blur-xl shadow-2xl flex items-center justify-center">
                  <i className="ri-shield-check-line text-8xl text-cyan-600"></i>
                </div>

                <div className="absolute top-20 right-20 w-16 h-16 bg-cyan-500/20 rounded-xl animate-bounce" style={{ animationDuration: '3s' }}></div>
                <div className="absolute bottom-20 left-20 w-20 h-20 bg-purple-500/20 rounded-xl animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}></div>
                
                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-cyan-500 rounded-full animate-ping"></div>
                <div className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-purple-500 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-gradient-to-b from-[#0A1628] via-[#1E3A5F] to-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="text-cyan-500 text-sm font-semibold tracking-wider uppercase mb-6">
            GET IN TOUCH
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Contact ZAGI DOO BOR
          </h2>
          <p className="text-[#B8C5D6] text-lg mb-16 max-w-2xl mx-auto">
            Have questions about our VPN service? We're here to help. Reach out to us through any of the channels below.
          </p>

          <div className="space-y-4 mb-12">
            <a 
              href="mailto:info@zagidoobor.com"
              className="block bg-white/5 backdrop-blur-xl rounded-2xl p-6 hover:bg-white/10 transition-all group cursor-pointer"
            >
              <div className="flex items-center gap-6">
                <div className="w-14 h-14 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <div className="text-left flex-grow">
                  <div className="text-[#B8C5D6] text-sm mb-1">Email</div>
                  <div className="text-white font-bold text-lg">info@zagidoobor.com</div>
                </div>
                <i className="ri-arrow-right-line text-2xl text-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity"></i>
              </div>
            </a>

            <a 
              href="tel:+17346009713"
              className="block bg-white/5 backdrop-blur-xl rounded-2xl p-6 hover:bg-white/10 transition-all group cursor-pointer"
            >
              <div className="flex items-center gap-6">
                <div className="w-14 h-14 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <div className="text-left flex-grow">
                  <div className="text-[#B8C5D6] text-sm mb-1">Phone</div>
                  <div className="text-white font-bold text-lg">+1 7346009713</div>
                </div>
                <i className="ri-arrow-right-line text-2xl text-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity"></i>
              </div>
            </a>

            <div className="block bg-white/5 backdrop-blur-xl rounded-2xl p-6">
              <div className="flex items-center gap-6">
                <div className="w-14 h-14 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div className="text-left flex-grow">
                  <div className="text-[#B8C5D6] text-sm mb-1">Address</div>
                  <div className="text-white font-bold text-lg">Save Kovacevica 29a, Bor 601907, Serbia</div>
                </div>
              </div>
            </div>
          </div>

          <button className="px-8 py-4 bg-transparent border-2 border-cyan-500 text-cyan-500 font-medium rounded-lg hover:bg-cyan-500 hover:text-white transition-all flex items-center gap-2 mx-auto whitespace-nowrap">
            Send Message
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/22b1e345-a75d-47ba-94cf-08e446d9b10f.png" 
                  alt="ZAGI DOO BOR Logo" 
                  className="h-10 w-10 object-contain"
                />
                <span className="font-bold text-lg whitespace-nowrap">ZAGI DOO BOR</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Securing Digital Freedom Worldwide
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4">Product</h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => scrollToSection('vpn')}
                    className="text-gray-400 hover:text-cyan-500 transition-colors text-sm cursor-pointer whitespace-nowrap"
                  >
                    VPN Features
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('security')}
                    className="text-gray-400 hover:text-cyan-500 transition-colors text-sm cursor-pointer whitespace-nowrap"
                  >
                    Security
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('how-it-works')}
                    className="text-gray-400 hover:text-cyan-500 transition-colors text-sm cursor-pointer whitespace-nowrap"
                  >
                    How It Works
                  </button>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-cyan-500 transition-colors text-sm whitespace-nowrap">
                    Download
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => scrollToSection('about')}
                    className="text-gray-400 hover:text-cyan-500 transition-colors text-sm cursor-pointer whitespace-nowrap"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('contact')}
                    className="text-gray-400 hover:text-cyan-500 transition-colors text-sm cursor-pointer whitespace-nowrap"
                  >
                    Contact
                  </button>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-cyan-500 transition-colors text-sm whitespace-nowrap">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-cyan-500 transition-colors text-sm whitespace-nowrap">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Connect</h4>
              <div className="flex gap-4">
                <a 
                  href="#" 
                  className="w-10 h-10 border border-gray-600 rounded-lg flex items-center justify-center hover:border-cyan-500 hover:text-cyan-500 transition-all cursor-pointer"
                >
                  <i className="ri-linkedin-line text-xl"></i>
                </a>
                <a 
                  href="#" 
                  className="w-10 h-10 border border-gray-600 rounded-lg flex items-center justify-center hover:border-cyan-500 hover:text-cyan-500 transition-all cursor-pointer"
                >
                  <i className="ri-twitter-x-line text-xl"></i>
                </a>
                <a 
                  href="#" 
                  className="w-10 h-10 border border-gray-600 rounded-lg flex items-center justify-center hover:border-cyan-500 hover:text-cyan-500 transition-all cursor-pointer"
                >
                  <i className="ri-facebook-line text-xl"></i>
                </a>
                <a 
                  href="#" 
                  className="w-10 h-10 border border-gray-600 rounded-lg flex items-center justify-center hover:border-cyan-500 hover:text-cyan-500 transition-all cursor-pointer"
                >
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2025 ZAGI DOO BOR. All rights reserved.
            </p>
            <a 
              href="https://readdy.ai/?origin=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 text-sm hover:text-cyan-500 transition-colors whitespace-nowrap"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
